import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Brain,
  ChevronRight,
  Copy,
  FileDown,
  FileText,
  Home,
  MoreHorizontal,
  SquarePen,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import ActionMenuItem from '../../components/AIChat/ActionMenuItem';
import ChatIconButton from '../../components/AIChat/ChatIconButton';
import { menuDropdown, ghostIconButton } from '../../components/AIChat/chatViewTheme';
import { dropdown } from '../../components/ui/uiTheme';
import { CHAT_MODE_OPTIONS } from '../../components/AIChat/ModePicker';

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `~${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `~${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

function StatusPill({
  loading,
  streamingMessageId,
}: {
  loading: boolean;
  streamingMessageId: string | null;
}) {
  let label = 'Ready';
  let dotClass = 'bg-emerald-400';
  let pillClass = 'text-emerald-300 border-emerald-900/60 bg-emerald-950/40';

  if (loading && streamingMessageId) {
    label = 'Streaming';
    dotClass = 'bg-sky-400';
    pillClass = 'text-sky-300 border-sky-900/60 bg-sky-950/40';
  } else if (loading) {
    label = 'Thinking…';
    dotClass = 'bg-amber-400';
    pillClass = 'text-amber-300 border-amber-900/60 bg-amber-950/40';
  }

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={label}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.92 }}
        transition={{ duration: 0.15 }}
        className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${pillClass}`}
      >
        <span
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass} ${loading ? 'animate-pulse' : ''}`}
          aria-hidden
        />
        {label}
      </motion.span>
    </AnimatePresence>
  );
}

function ScopeBreadcrumb({
  scopeChip,
  scopeOverride,
  navigationContextEnabled,
  onOpenScopePicker,
  onClearScope,
}: {
  scopeChip: string;
  scopeOverride: any;
  navigationContextEnabled: boolean;
  onOpenScopePicker: () => void;
  onClearScope?: () => void;
}) {
  if (!navigationContextEnabled) return null;

  const parts = scopeChip ? scopeChip.split(' · ').filter(Boolean) : [];

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap py-1">
      <span className="shrink-0 text-[10px] font-medium uppercase tracking-wider text-slate-600">
        Context
      </span>
      <ChevronRight className="h-2.5 w-2.5 shrink-0 text-slate-700" aria-hidden />

      {parts.length > 0 ? (
        <>
          {parts.map((part, i) => (
            <React.Fragment key={part}>
              <span className="flex items-center gap-1 rounded-full border border-dark-border bg-dark-elevated px-2.5 py-0.5 text-[11px] text-slate-300">
                {part}
                {i === parts.length - 1 && scopeOverride && onClearScope ? (
                  <button
                    type="button"
                    onClick={onClearScope}
                    className="ml-0.5 rounded-full p-0.5 text-slate-600 hover:text-slate-400"
                    aria-label="Clear scope"
                  >
                    <X className="h-2.5 w-2.5" aria-hidden />
                  </button>
                ) : null}
              </span>
              {i < parts.length - 1 && (
                <ChevronRight className="h-2.5 w-2.5 shrink-0 text-slate-700" aria-hidden />
              )}
            </React.Fragment>
          ))}
          <button
            type="button"
            onClick={onOpenScopePicker}
            className="shrink-0 rounded border border-dark-border bg-dark-elevated px-2 py-0.5 text-[10px] font-medium text-slate-500 transition-all hover:border-brand-purple/40 hover:text-brand-purple-light"
          >
            Change
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={onOpenScopePicker}
          className="rounded-full border border-dashed border-dark-border px-2.5 py-0.5 text-[11px] text-slate-600 transition-all hover:border-brand-purple/40 hover:text-brand-purple-light"
        >
          + Pick scope
        </button>
      )}
    </div>
  );
}

interface VantiWindowHeaderProps {
  threadTitle: string;
  messagesCount: number;
  lastActivityAt?: string | null;
  onClearChat: () => void;
  actionMenuRef: React.RefObject<any>;
  actionMenuOpen: boolean;
  onToggleActionMenu: () => void;
  memoryPanelOpen: boolean;
  onToggleMemory: () => void;
  hasExportableMessages: boolean;
  onCopyConversation: () => void;
  onExportMarkdown: () => void;
  onExportPdf: () => void;
  selectedAgentId: string;
  onAgentChange: (e: any) => void;
  agents: any[];
  loading: boolean;
  streamingMessageId: string | null;
  chatMode: string;
  navigationContextEnabled: boolean;
  scopeChip: string;
  scopeOverride: any;
  onOpenScopePicker: () => void;
  onClearScope?: () => void;
  ragContextEnabled: boolean;
  indexHint: string;
}

export default function VantiWindowHeader({
  threadTitle,
  messagesCount,
  lastActivityAt,
  onClearChat,
  actionMenuRef,
  actionMenuOpen,
  onToggleActionMenu,
  memoryPanelOpen,
  onToggleMemory,
  hasExportableMessages,
  onCopyConversation,
  onExportMarkdown,
  onExportPdf,
  selectedAgentId,
  onAgentChange,
  agents,
  loading,
  streamingMessageId,
  chatMode,
  navigationContextEnabled,
  scopeChip,
  scopeOverride,
  onOpenScopePicker,
  onClearScope,
  ragContextEnabled,
  indexHint,
}: VantiWindowHeaderProps) {
  const modeLabel = CHAT_MODE_OPTIONS.find((m) => m.id === chatMode)?.label ?? 'Auto';
  const rel = relativeTime(lastActivityAt);

  return (
    <div className="shrink-0 border-b border-dark-border bg-dark-surface">
      {/* Title row */}
      <div className="flex items-start justify-between gap-3 px-5 py-3">
        {/* Left: title + metadata */}
        <div className="min-w-0 flex-1">
          <AnimatePresence mode="wait">
            {threadTitle ? (
              <motion.h1
                key={threadTitle}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="truncate text-sm font-semibold text-slate-100"
                title={threadTitle}
              >
                {threadTitle}
              </motion.h1>
            ) : (
              <motion.h1
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm font-medium text-slate-500"
              >
                New conversation
              </motion.h1>
            )}
          </AnimatePresence>

          {/* Metadata: mode · count · recency */}
          {messagesCount > 0 && (
            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-600">
              <span>{modeLabel}</span>
              <span aria-hidden>·</span>
              <span>{messagesCount} {messagesCount === 1 ? 'message' : 'messages'}</span>
              {rel && (
                <>
                  <span aria-hidden>·</span>
                  <span>{rel}</span>
                </>
              )}
            </p>
          )}
        </div>

        {/* Right: status pill + controls */}
        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/"
            className={`${ghostIconButton} rounded-lg p-2 transition-colors bg-dark-elevated hover:text-brand-teal-light`}
            aria-label="Go to dashboard"
            title="Dashboard"
          >
            <Home className="h-4 w-4" aria-hidden />
          </Link>
          <StatusPill loading={loading} streamingMessageId={streamingMessageId} />

          <motion.button
            type="button"
            onClick={onClearChat}
            disabled={messagesCount === 0}
            whileHover={messagesCount === 0 ? undefined : { scale: 1.08 }}
            whileTap={messagesCount === 0 ? undefined : { scale: 0.92 }}
            className={`rounded-lg p-2 transition-colors ${
              messagesCount === 0
                ? 'cursor-not-allowed text-slate-700 opacity-30'
                : 'text-slate-400 hover:bg-dark-elevated hover:text-slate-200'
            }`}
            aria-label="New conversation"
            title="New conversation"
          >
            <SquarePen className="h-4 w-4" aria-hidden />
          </motion.button>

          <div ref={actionMenuRef} className="relative">
            <ChatIconButton
              onClick={onToggleActionMenu}
              label="Chat options"
              title="Options"
              className={actionMenuOpen ? 'bg-dark-elevated text-slate-200' : ''}
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden />
            </ChatIconButton>
            <AnimatePresence>
              {actionMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -4 }}
                  transition={{ duration: 0.12, ease: 'easeOut' }}
                  className={`absolute right-0 top-full z-20 mt-1.5 w-52 origin-top-right ${menuDropdown}`}
                >
                  <ActionMenuItem
                    icon={Brain}
                    label="Vanti memory"
                    variant="green"
                    active={memoryPanelOpen}
                    onClick={onToggleMemory}
                  />
                  <div className={dropdown.separator} />
                  <ActionMenuItem
                    icon={Copy}
                    label="Copy conversation"
                    variant="purple"
                    disabled={!hasExportableMessages}
                    onClick={onCopyConversation}
                  />
                  <ActionMenuItem
                    icon={FileText}
                    label="Export as Markdown"
                    variant="purple"
                    disabled={!hasExportableMessages}
                    onClick={onExportMarkdown}
                  />
                  <ActionMenuItem
                    icon={FileDown}
                    label="Export as PDF"
                    variant="purple"
                    disabled={!hasExportableMessages}
                    onClick={onExportPdf}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Agent + breadcrumb row */}
      <div className="flex items-center gap-3 border-t border-dark-border px-5 py-2">
        {/* Agent selector */}
        <div className="w-40 shrink-0">
          <label className="sr-only" htmlFor="vanti-window-agent-select">Agent</label>
          <select
            id="vanti-window-agent-select"
            className="select-mdl w-full text-xs leading-normal"
            value={selectedAgentId}
            onChange={onAgentChange}
            disabled={loading}
            title="Select agent"
          >
            <option value="">Vanti (built-in)</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}{a.visibility === 'org' ? ' (Org)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Breadcrumb scope */}
        <div className="min-w-0 flex-1">
          <ScopeBreadcrumb
            scopeChip={scopeChip}
            scopeOverride={scopeOverride}
            navigationContextEnabled={navigationContextEnabled}
            onOpenScopePicker={onOpenScopePicker}
            onClearScope={onClearScope}
          />
        </div>

        {/* RAG / index hints */}
        {(!ragContextEnabled || indexHint) ? (
          <div className="shrink-0 space-y-0.5">
            {!ragContextEnabled && (
              <p className="flex items-center gap-1 truncate text-[10px] text-amber-300">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" aria-hidden />
                Code/docs search off
              </p>
            )}
            {indexHint && (
              <p className="flex items-center gap-1 truncate text-[10px] text-slate-500">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-dark-border" aria-hidden />
                {indexHint}
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
